<template>
  <div class="relative"
    @mouseover="mouseOver = true"
    @mouseleave="mouseOver = false">
    <button
      class="flex items-center justify-between w-full py-2 pl-3 pr-4 text-gray-400 border-b border-gray-100 bg-gray-200 md:bg-transparent hover:bg-gray-50 md:hover:bg-transparent md:border-0 md:hover:text-gray-200 md:p-0 md:w-auto"
      aria-current="page"
      @click="onMenuClick">
      Viewing -- {{ hoverTextDisplay }}
      <svg class="w-5 h-5 ml-1" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    </button>
    <!-- Dropdown menu -->
    <div
      id="dropdownNavbar"
      class="absolute top-8 left-8 z-20 text-left font-normal bg-white divide-y divide-gray-100 rounded-sm shadow w-96"
      :class="{
        'hidden': !mouseOver
      }">
      <div class="py-1 text-xs text-gray-700">
        <div class="flex flex-col p-2 mb-1 gap-1">
          <Button
            class="rounded-sm w-full p-2 mr-1"
            :color="userCanChangeView ? 'blue' : 'gray'"
            @click="onChangeViewSelected()"
            :disabled="!userCanChangeView">
            Change Data View
          </Button>
          <div class="w-full border-b-2 pt-2">
            <span class="pl-3">Current Data View</span>
          </div>
          <div class="my-1 ml-2"
            v-for="data in displayData"
            :key="data.key">
            <div class="grid grid-cols-5">
              <span class="col-span-1">{{ data.key }}</span>
              <div class="flex flex-col col-span-4">
                <ItemDisplay
                  v-for="itemValues in data.value"
                  :key="itemValues.id"
                  :id="itemValues.id"
                  :type="itemValues.type">
                </ItemDisplay>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '../../Button.vue'
import ItemDisplay from './ItemDisplay.vue'

import user from '../../../services/user/user.js'
import view from '../../../services/user/view.js'
import events from '../../../services/events.js'

import buildingData from '../../../services/viewData/building.js'
import districtData from '../../../services/viewData/district.js'
import partitionData from '../../../services/viewData/partition.js'
import schoolYearData from '../../../services/viewData/schoolYear.js'
import vendorData from '../../../services/viewData/vendor.js'

import DROPDOWN_LISTS from '../../../components/DropdownLists.json'

const DISPLAY_MAP = {
  schoolYear: 'School Year',
  partition: 'Partition',
  district: 'District',
  vendor: 'Vendor',
  building: 'Building',
}

export default {
  components: {
    Button,
    ItemDisplay,
  },
  props: {
    title: String,
  },
  data() {
    return {
      user: undefined,
      view: undefined,

      hoverTextData: [{
        key: undefined,
        values: ['No View Loaded']
      }],
      userCanChangeView: false,

      mouseOver: false,
      hideMenu: true,
    }
  },
  eventCallbackIds: {},
  methods: {
    onChangeViewSelected() {
      this.$emit('openChangeView')
    },
    closeMenu() {
      this.hideMenu = true
    },
    openMenu() {
      this.hideMenu = false
    },
    onMenuClick() {
      this.hideMenu = !this.hideMenu
    },
    onLoseFocus() {
      this.closeMenu()
    },
    prepareHoverTextData(newView) {
      if (newView === undefined) {
        this.hoverTextData = [{ key: undefined, values: ['No View Loaded']}]
        return
      }
      let viewRef = newView.getRef()

      let schoolYearPromise = (viewRef.schoolYear) ? schoolYearData.filter(viewRef.schoolYear) : Promise.resolve(undefined)
      let partitionPromise = (viewRef.partition) ? partitionData.filter(viewRef.partition) : Promise.resolve(undefined)
      let districtPromise = (viewRef.district) ? districtData.filter(viewRef.district) : Promise.resolve(undefined)
      let buildingPromise = (viewRef.building) ? buildingData.filter(viewRef.building) : Promise.resolve(undefined)
      let vendorPromise = (viewRef.vendor) ? vendorData.filter(viewRef.vendor) : Promise.resolve(undefined)

      Promise.all([
        schoolYearPromise,
        partitionPromise,
        districtPromise,
        buildingPromise,
        vendorPromise,
      ]).then(([_schoolYearData, _partitionData, _districtData, _buildingData, _vendorData]) => {
        this.hoverTextData = []

        if (_schoolYearData) {
          this.hoverTextData.push({
            key: 'schoolYear',
            values: _schoolYearData.map(elem => DROPDOWN_LISTS.schoolYears[elem.code])
          })
        }
        if (_partitionData) {
          this.hoverTextData.push({
            key: 'partition',
            values: _partitionData.map(elem => elem.data.description)
          })
        }
        if (_districtData) {
          this.hoverTextData.push({
            key: 'district',
            values: _districtData.map(elem => elem.data.name)
          })
        }
        if (_buildingData) {
          this.hoverTextData.push({
            key: 'building',
            values: _buildingData.map(elem => elem.data.name)
          })
        }
        if (_vendorData) {
          this.hoverTextData.push({
            key: 'vendor',
            values: _vendorData.map(elem => elem.data.name)
          })
        }
      })

      this.hoverTextData = []
      for (const key in DISPLAY_MAP) {
        if (viewRef[key]) {
          this.hoverTextData.push({
            key: key,
            values: viewRef[key].map(elem => `Loading (${elem})`)
          })
        }
      }

      if (this.hoverTextData.length === 0) {
        this.hoverTextData = [{ key: undefined, values: ['All Data Displaying']}]
      }
    }
  },
  computed: {
    displayData() {
      if (this.view === undefined) return []

      let dataRef = this.view.getRef()
      let _display = []

      for (const key in DISPLAY_MAP) {
        if (dataRef[key] && dataRef[key].length > 0) {
          // let _value = ''
          // for (const value of dataRef[key].slice(0, dataRef[key].length - 1)) {
          //   _value += `${value}, `
          // }
          // _value += dataRef[key].at(-1)
          let _value = []
          for (const _id of dataRef[key]) {
            _value.push({
              id: _id,
              type: key,
            })
          }

          _display.push({
            key: DISPLAY_MAP[key],
            value: _value,
          })
        }
      }

      return _display
    },
    hoverTextDisplay() {
      return this.hoverTextData
        .filter(viewSection => viewSection.values.length > 0)
        .map(viewSection => {
          if (viewSection.key) {
            return `${DISPLAY_MAP[viewSection.key]}: ${viewSection.values.join(', ')}`
          }
          else {
            return viewSection.values.join('')
          }
        })
        .join('; ')
    },
  },
  async mounted() {
    this.user = await user.get()
    this.view = await view.get()

    this.userCanChangeView = this.user.can('change_view')
    this.prepareHoverTextData(this.view)
  },
  watch: {
    view(newValue) {
      this.prepareHoverTextData(newValue)
    }
  },
  created() {
    this.$options.eventCallbackIds.viewUpdated = events.register('viewUpdated', async () => {
      this.view = await view.get()
      this.prepareHoverTextData(this.view)
    })
  },
  //beforeUnmount() {
  // eslint-disable-next-line
  beforeDestroy() {
    events.remove('viewUpdated', this.$options.eventCallbackIds.viewUpdated)
  }
}
</script>
